/**
 * AppSettingsPage — superAdmin-only editor for the appSetting key/value store.
 *
 * Keys are immutable (managed by migrations); only values are editable here.
 * Settings are grouped visually by category. Edits are saved one row at a
 * time and the in-memory context cache is refreshed on success so the rest
 * of the app (Help menu, HelpPage, etc.) picks up the new value immediately.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Card,
  Label,
  Spinner,
  Textarea,
  TextInput,
} from 'flowbite-react';
import { HiCog } from 'react-icons/hi';

import { appSettingsApi, type AppSetting } from '../api/appSettings';
import { useAuth } from '../contexts/AuthContext';
import { useAppSettings } from '../contexts/AppSettingsContext';
import { getUserErrorMessage } from '../api/client';

const CATEGORY_LABELS: Record<string, string> = {
  support: 'Support Contact',
  help: 'Help Resources',
};

const prettyCategory = (category: string | null): string => {
  if (!category) return 'Other';
  return CATEGORY_LABELS[category] ?? category;
};

/** Use a Textarea for any value long enough or containing newlines. */
const isLongValue = (value: string | null): boolean => {
  if (!value) return false;
  return value.length > 80 || value.includes('\n');
};

export const AppSettingsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { refresh: refreshContext } = useAppSettings();

  // Redirect non-superAdmin users.
  useEffect(() => {
    if (currentUser && currentUser.role !== 'superAdmin') {
      navigate('/access-denied');
    }
  }, [currentUser, navigate]);

  const [rows, setRows] = useState<AppSetting[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await appSettingsApi.list();
      setRows(data);
      // Seed drafts from current values
      const initial: Record<string, string> = {};
      for (const row of data) {
        initial[row.settingKey] = row.settingValue ?? '';
      }
      setDrafts(initial);
    } catch (err) {
      setError(getUserErrorMessage(err, 'Failed to load app settings'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const grouped = useMemo(() => {
    const byCategory = new Map<string, AppSetting[]>();
    for (const row of rows) {
      const key = row.category ?? '__other__';
      if (!byCategory.has(key)) byCategory.set(key, []);
      byCategory.get(key)!.push(row);
    }
    return Array.from(byCategory.entries()).map(([category, items]) => ({
      category: category === '__other__' ? null : category,
      items: [...items].sort((a, b) => a.sortOrder - b.sortOrder),
    }));
  }, [rows]);

  const handleSave = async (settingKey: string) => {
    const row = rows.find(r => r.settingKey === settingKey);
    if (!row) return;

    const draft = drafts[settingKey] ?? '';
    const newValue = draft === '' ? null : draft;

    if ((row.settingValue ?? '') === (newValue ?? '')) {
      // No-op; nothing to save
      return;
    }

    setSavingKey(settingKey);
    setError(null);
    setSuccessMessage(null);
    try {
      const updated = await appSettingsApi.update(settingKey, newValue);
      setRows(prev => prev.map(r => (r.settingKey === settingKey ? updated : r)));
      setSuccessMessage(`Saved "${settingKey}"`);
      // Refresh the global cache so the UserMenu / HelpPage pick up the change.
      void refreshContext();
    } catch (err) {
      setError(getUserErrorMessage(err, `Failed to save "${settingKey}"`));
    } finally {
      setSavingKey(null);
    }
  };

  const handleReset = (settingKey: string) => {
    const row = rows.find(r => r.settingKey === settingKey);
    if (!row) return;
    setDrafts(prev => ({ ...prev, [settingKey]: row.settingValue ?? '' }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-8 max-w-5xl">
      <div className="mb-8 flex items-center">
        <HiCog className="w-8 h-8 mr-3 text-gray-700 dark:text-gray-300" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">App Settings</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Runtime-configurable application settings. Keys are managed by migrations; only
            values can be edited here.
          </p>
        </div>
      </div>

      {error && (
        <Alert color="failure" className="mb-4" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert color="success" className="mb-4" onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {grouped.map(group => (
        <Card key={group.category ?? 'other'} className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {prettyCategory(group.category)}
          </h2>
          <div className="space-y-6">
            {group.items.map(row => {
              const draft = drafts[row.settingKey] ?? '';
              const dirty = (row.settingValue ?? '') !== draft;
              const saving = savingKey === row.settingKey;
              const useTextarea = isLongValue(row.settingValue) || isLongValue(draft);

              return (
                <div
                  key={row.settingKey}
                  className="pb-6 border-b border-gray-200 dark:border-gray-700 last:border-b-0 last:pb-0"
                >
                  <Label
                    htmlFor={`app-setting-${row.settingKey}`}
                    className="font-mono text-xs text-gray-500 dark:text-gray-400"
                  >
                    {row.settingKey}
                  </Label>
                  {row.description && (
                    <p className="mt-1 mb-2 text-sm text-gray-700 dark:text-gray-300">
                      {row.description}
                    </p>
                  )}
                  {useTextarea ? (
                    <Textarea
                      id={`app-setting-${row.settingKey}`}
                      rows={Math.min(10, Math.max(3, draft.split('\n').length + 1))}
                      value={draft}
                      onChange={e =>
                        setDrafts(prev => ({ ...prev, [row.settingKey]: e.target.value }))
                      }
                      className="font-mono text-sm"
                    />
                  ) : (
                    <TextInput
                      id={`app-setting-${row.settingKey}`}
                      type="text"
                      value={draft}
                      onChange={e =>
                        setDrafts(prev => ({ ...prev, [row.settingKey]: e.target.value }))
                      }
                    />
                  )}
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleSave(row.settingKey)}
                      disabled={!dirty || saving}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded"
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleReset(row.settingKey)}
                      disabled={!dirty || saving}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                    >
                      Reset
                    </button>
                    {row.modifiedAt && (
                      <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
                        Last updated {new Date(row.modifiedAt).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      ))}
    </div>
  );
};
